import {Component, computed, effect, inject, input, output} from '@angular/core';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  ProviderTenderControllerService,
  ProviderUserControllerService,
  ProviderUserResponse,
  RejectTenderRequestParams,
  TenderDetailResponse,
  TenderPhaseType,
} from '@frontend/shared-api';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {RadioButton} from 'primeng/radiobutton';
import {Button} from 'primeng/button';
import {FormFieldComponent} from '../../../../../shared/components/inputs/form-field/form-field.component';
import {firstValueFrom} from 'rxjs';
import {SubsectionTitleComponent} from '../../../../../shared/components/header/subsection-title/subsection-title.component';
import {LoadingComponent} from '../../../../../shared/components/loading/loading.component';
import {TenderRejectionReasonComponent} from '../tender-rejection-reason/tender-rejection-reason.component';
import {ProviderContactOptionVM, TenderDecision} from './tender-confirmation.types';
import {AppDatePipe} from '@frontend/shared';

@Component({
  selector: 'app-tender-confirmation',
  imports: [
    TranslocoPipe,
    ReactiveFormsModule,
    RadioButton,
    Button,
    FormFieldComponent,
    SubsectionTitleComponent,
    LoadingComponent,
    TenderRejectionReasonComponent,
    AppDatePipe,
  ],
  templateUrl: './tender-confirmation.component.html',
})
export class TenderConfirmationComponent {
  protected readonly TenderDecision = TenderDecision;

  private readonly providerUserController = inject(ProviderUserControllerService);
  private readonly fb = inject(FormBuilder);
  private readonly providerTenderController = inject(ProviderTenderControllerService);

  protected readonly decisionForm = this.fb.group({
    decision: [TenderDecision.DECLINE, Validators.required],
    tenderContactPersonUuid: ['', Validators.required],
    tenderRejectionReasonId: ['', Validators.required],
  });

  protected readonly decisionControl = this.decisionForm.controls.decision;

  readonly tenderDetails = input.required<TenderDetailResponse>();
  tenderPhase = input.required<TenderPhaseType>();
  accepted = output<void>();
  rejected = output<void>();

  private readonly decisionValue = toSignal(this.decisionControl.valueChanges, {
    initialValue: TenderDecision.DECLINE,
  });

  protected readonly tenderId = computed(() => this.tenderDetails().tenderId);
  protected readonly isAccepting = computed(() => this.decisionValue() === TenderDecision.ACCEPT);
  protected readonly tenderPhaseLowerCase = computed(() => this.tenderPhase()?.toLowerCase());

  protected readonly dynamicAcceptText = computed(() => {
    return `provider.tender.confirmation.${this.tenderPhaseLowerCase()}.acceptText`;
  });

  protected readonly dynamicDeclineText = computed(() => {
    return `provider.tender.confirmation.${this.tenderPhaseLowerCase()}.declineText`;
  });

  readonly providerContactsResource = rxResource({
    stream: () => this.providerUserController.getAllActiveProviderUsers(),
  });

  private readonly existingContactPersonResource = rxResource({
    params: () => this.tenderId(),
    stream: ({params: request}) =>
      this.providerTenderController.getTenderContactPerson({tenderId: request}),
  });

  readonly providerContacts = computed(
    () => this.providerContactsResource.value() ?? ([] as ProviderUserResponse[]),
  );

  readonly providerContactOptions = computed<ProviderContactOptionVM[]>(() =>
    this.providerContacts().map((contact) => ({
      label: contact.firstName + ' ' + contact.lastName,
      value: contact.uuid,
    })),
  );

  protected readonly isLoading = computed(
    () =>
      this.providerContactsResource.isLoading() || this.existingContactPersonResource.isLoading(),
  );

  constructor() {
    effect(() => {
      this.syncDecisionDependentControls();
    });

    effect(() => {
      this.patchExistingContactPerson();
    });
  }

  protected async acceptTender(): Promise<void> {
    await firstValueFrom(
      this.providerTenderController.acceptTender({
        tenderId: this.tenderId(),
        tenderPhase: this.tenderPhase(),
      }),
    );
    await this.upsertProviderContactPerson();
    this.accepted.emit();
  }

  protected async rejectTender(): Promise<void> {
    const request: RejectTenderRequestParams = {
      tenderId: this.tenderId(),
      tenderRejectionRequest: {
        tenderPhase: this.tenderPhase(),
        reasonDirectusUuid: this.decisionForm.controls.tenderRejectionReasonId.value ?? '',
      },
    };
    await firstValueFrom(this.providerTenderController.rejectTender(request));
    this.rejected.emit();
  }

  private patchExistingContactPerson(): void {
    const existingContactPerson = this.existingContactPersonResource.value();
    this.decisionForm.patchValue({
      tenderContactPersonUuid: existingContactPerson?.providerUserUuid ?? '',
    });
  }

  private async upsertProviderContactPerson(): Promise<void> {
    await firstValueFrom(
      this.providerTenderController.updateTenderContactPerson({
        tenderId: this.tenderId(),
        body: this.decisionForm.get('tenderContactPersonUuid')?.value ?? '',
      }),
    );
  }

  private syncDecisionDependentControls(): void {
    const contactControl = this.decisionForm.controls.tenderContactPersonUuid;
    const reasonControl = this.decisionForm.controls.tenderRejectionReasonId;
    if (this.isAccepting()) {
      contactControl.enable({emitEvent: false});
      reasonControl.disable({emitEvent: false});
    } else {
      contactControl.disable({emitEvent: false});
      reasonControl.enable({emitEvent: false});
    }
  }
}
